// 下注动画：抽取自原始[id].vue和IndoorHeader.vue的下注动画和逻辑功能
import { ref } from 'vue';
import gsapDefault from 'gsap';
import svgFilePath from '~/assets/svg/all-icons.svg';

// 筹码 PNG 图片
import chip1Png from '@/assets/imgs/betcorgi/chip1.png';
import chip2Png from '@/assets/imgs/betcorgi/chip2.png';
import chip5Png from '@/assets/imgs/betcorgi/chip5.png';
import chip10Png from '@/assets/imgs/betcorgi/chip10.png';
import chip50Png from '@/assets/imgs/betcorgi/chip50.png';
import chip100Png from '@/assets/imgs/betcorgi/chip100.png';
import chip200Png from '@/assets/imgs/betcorgi/chip200.png';
import chip500Png from '@/assets/imgs/betcorgi/chip500.png';

// 🔧 Safari SVG 兼容性：将外部 SVG symbol 内联到 DOM
let svgSymbolsInlined = false;
async function ensureSvgSymbolsInlined(): Promise<void> {
  if (svgSymbolsInlined) return;
  if (typeof document === 'undefined') return;
  
  // 检测 Safari
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (!isSafari) {
    svgSymbolsInlined = true;
    return;
  }
  
  // 防止重复注入
  if (document.querySelector('[data-inline-svg="all-icons"]')) {
    svgSymbolsInlined = true;
    return;
  }
  
  try {
    const res = await fetch(svgFilePath);
    const text = await res.text();
    
    // 创建隐藏的 SVG 容器
    const container = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    container.setAttribute('data-inline-svg', 'all-icons');
    container.setAttribute('aria-hidden', 'true');
    Object.assign(container.style, {
      position: 'absolute',
      width: '0',
      height: '0',
      overflow: 'hidden',
      visibility: 'hidden',
      pointerEvents: 'none',
      zIndex: '-1',
    });
    
    // 提取 <symbol> 元素
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    const innerSvg = tempDiv.querySelector('svg');
    if (innerSvg) {
      const symbols = innerSvg.querySelectorAll('symbol');
      symbols.forEach(sym => container.appendChild(sym.cloneNode(true)));
    }
    
    document.body.appendChild(container);
    svgSymbolsInlined = true;
    console.log('[bettingAnimation] Safari SVG symbols inlined successfully');
  } catch (err) {
    console.warn('[bettingAnimation] Failed to inline SVG symbols:', err);
  }
}

// 导出供外部调用
export { ensureSvgSymbolsInlined };

// 模块加载时自动执行（仅客户端）
if (typeof window !== 'undefined') {
  ensureSvgSymbolsInlined();
}

// GSAP 模块缓存
let gsap: any = null;

// 性能优化配置
const PERF_CONFIG = {
  MAX_CHIPS_PER_BET: 15,        // 降低：平衡显示效果和性能
  CHIP_POOL_SIZE: 30,           // 保持：对象池不影响实时性能
  MAX_CONCURRENT_ANIMS: 12,     // 降低：更保守的并发限制
  RAF_BATCH_SIZE: 3,            
  MIN_FPS: 30,                  
  TURBO_MODE_CHIPS: 8,          // 降低：高负载时更激进限流
  MAX_CHIPS_PER_AREA: 40       // 每个区域最多显示30个筹码DOM，超出时删除旧筹码
} as const;

// 筹码DOM对象池（核心优化）
class ChipPool {
  private pool: Map<number, HTMLElement[]> = new Map();
  private stats = { created: 0, reused: 0 };
  
  acquire(chipValue: number): HTMLElement | null {
    let chips = this.pool.get(chipValue) || [];
    let chip = chips.pop();
    
    if (chip) {
      this.stats.reused++;
      chip.style.display = 'flex';
      return chip;
    }
    
    // 池中没有，创建新的
    chip = this.createChip(chipValue);
    if (chip) this.stats.created++;
    return chip;
  }
  
  release(chip: HTMLElement, chipValue: number) {
    chip.style.display = 'none';
    chip.style.opacity = '0';
    
    const chips = this.pool.get(chipValue) || [];
    if (chips.length < PERF_CONFIG.CHIP_POOL_SIZE) {
      chips.push(chip);
      this.pool.set(chipValue, chips);
    } else {
      chip.remove(); // 池满，销毁
    }
  }
  
  private createChip(chipValue: number): HTMLElement | null {
    try {
      const chipElement = cloneChipSvg(chipValue, '18.8');
      if (!chipElement) return null;
      
      const container = document.createElement('div');
      container.className = 'flying-chip-pooled';
      container.style.cssText = 'position:fixed;width:18.8px;height:18.8px;display:none;';
      container.appendChild(chipElement);
      document.body.appendChild(container);
      return container;
    } catch (e) {
      return null;
    }
  }
  
  clear() {
    this.pool.forEach(chips => chips.forEach(c => c.remove()));
    this.pool.clear();
    console.log(`对象池已清理 (创建${this.stats.created}, 复用${this.stats.reused})`);
  }
  
  getStats() {
    const rate = this.stats.created > 0 
      ? Math.round((this.stats.reused / this.stats.created) * 100) 
      : 0;
    return { ...this.stats, reuseRate: rate + '%' };
  }
}

const chipPool = new ChipPool();

// � 永久筹码对象池（用于批量创建场景）
class PermanentChipPool {
  private pool: Map<number, HTMLElement[]> = new Map();
  private stats = { created: 0, reused: 0 };
  
  acquire(chipValue: number): HTMLElement | null {
    let chips = this.pool.get(chipValue) || [];
    let container = chips.pop();
    
    if (container) {
      this.stats.reused++;
      container.style.display = 'flex';
      return container;
    }
    
    // 池中没有，返回null（由调用者创建）
    return null;
  }
  
  release(container: HTMLElement, chipValue: number) {
    container.style.display = 'none';
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
    
    const chips = this.pool.get(chipValue) || [];
    if (chips.length < 20) {
      chips.push(container);
      this.pool.set(chipValue, chips);
    }
  }
  
  clear() {
    this.pool.forEach(chips => chips.forEach(c => c.remove()));
    this.pool.clear();
    console.log(`[永久筹码池] 已清理 (创建${this.stats.created}, 复用${this.stats.reused})`);
  }
}

const permanentChipPool = new PermanentChipPool();

// �🎬 RAF动画队列管理器
class RAFAnimationScheduler {
  private queue: Array<() => void> = [];
  private isProcessing = false;
  
  // 添加动画到队列
  enqueue(animFn: () => void) {
    this.queue.push(animFn);
    if (!this.isProcessing) {
      this.process();
    }
  }
  
  // RAF批量处理
  private process() {
    if (this.queue.length === 0) {
      this.isProcessing = false;
      return;
    }
    
    this.isProcessing = true;
    
    requestAnimationFrame(() => {
      // 监控帧率
      const now = performance.now();
      const deltaTime = now - lastFrameTime;
      lastFrameTime = now;
      currentFPS = 1000 / deltaTime;
      
      // 🚨 帧率过低，启动涡轮模式
      if (currentFPS < PERF_CONFIG.MIN_FPS && !isTurboMode) {
        isTurboMode = true;
      } else if (currentFPS > PERF_CONFIG.MIN_FPS + 10 && isTurboMode) {
        isTurboMode = false;
      }
      
      // 根据当前活跃动画数量决定本帧处理多少个
      const batchSize = activeAnimCount > PERF_CONFIG.MAX_CONCURRENT_ANIMS 
        ? 1 
        : PERF_CONFIG.RAF_BATCH_SIZE;
      
      // 批量执行
      for (let i = 0; i < batchSize && this.queue.length > 0; i++) {
        const animFn = this.queue.shift();
        if (animFn) animFn();
      }
      
      // 继续处理剩余队列
      this.process();
    });
  }
  
  clear() {
    this.queue = [];
    this.isProcessing = false;
  }
  
  getStats() {
    return {
      queueLength: this.queue.length,
      activeAnims: activeAnimCount,
      fps: Math.round(currentFPS),
      turboMode: isTurboMode
    };
  }
}

const rafScheduler = new RAFAnimationScheduler();

// 动画配置常量
const ANIMATION_CONFIG = {
  CHIP_SIZE: 15,              // 筹码尺寸（像素）
  SAFETY_MARGIN: 3,           // 安全边距（像素）
  BETTING_AREA_HEIGHT: 35,    // 下注区域高度
  FLYING_CHIP_SIZE: 24,       // 飞行筹码尺寸（像素）
  PERMANENT_CHIP_SIZE: 18.8,    // 永久筹码尺寸（像素）
  ANIMATION_DURATION_MIN: 0.3,// 动画最小时长（秒）
  ANIMATION_DURATION_MAX: 0.4,// 动画最大时长（秒）
  FINAL_SCALE: 0.55,          // 最终缩放比例
  Z_INDEX_FLYING: '2',       // 飞行筹码层级
  Z_INDEX_PERMANENT: '3'      // 永久筹码层级（低于统计浮层的5）
} as const;

// 类型定义
export interface BetRecord {
  id: string;
  area: string;
  value: number;
  chipIcon: string;
  timestamp: string;
}

// 统一使用拼音ID，与tables配置、DOM、服务器保持一致
export type AreaId = 'tian' | 'di' | 'xuan' | 'huang';

export interface Player {
  name: string;
  avatar: string;
  chipPreference: number[];
}

// 响应式状态 - 使用拼音ID作为键
// areaTotalValues: 只包含当前玩家的下注（用于统计浮层显示）
export const areaTotalValues = ref<Record<AreaId, number>>({
  tian: 0,
  di: 0,
  xuan: 0,
  huang: 0
});

// allPlayersTotalValues: 包含所有玩家的下注总额（用于底部区域名称显示）
export const allPlayersTotalValues = ref<Record<AreaId, number>>({
  tian: 0,
  di: 0,
  xuan: 0,
  huang: 0
});

export const activeBets = ref<BetRecord[]>([]);

// 📊 性能监控
let activeAnimCount = 0;
let isTurboMode = false;
let lastFrameTime = performance.now();
let currentFPS = 60;

/**
 * 加载GSAP模块 - 带缓存和错误处理的异步函数
 */
export const loadGSAP = async () => {
  // 如果已经加载过GSAP，直接返回缓存的实例
  if (gsap) {
    return gsap;
  }
  
  try {
    gsap = gsapDefault;
    
    return gsap;
  } catch (error) {
    console.error('GSAP 模块加载失败:', error instanceof Error ? error.message : '未知错误');
    throw new Error('无法加载动画库，请检查依赖安装或刷新页面');
  }
};

/**
 * 计算筹码最终显示位置
 * 严格限制在betting-area区域内
 */
export const calculateChipFinalPosition = (targetArea: Element, chipValue: number, targetRect: DOMRect) => {
  try {
    // 重要：betting-area高度为40px，使用最严格的限制
    
    // 直接从原始元素获取样式信息
    const computedStyle = window.getComputedStyle(targetArea);
    const paddingLeft = parseInt(computedStyle.paddingLeft) || 0;
    const paddingTop = parseInt(computedStyle.paddingTop) || 0;
    const paddingRight = parseInt(computedStyle.paddingRight) || 0;
    const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
    const borderLeft = parseInt(computedStyle.borderLeftWidth) || 0;
    const borderTop = parseInt(computedStyle.borderTopWidth) || 0;
    const borderRight = parseInt(computedStyle.borderRightWidth) || 0;
    const borderBottom = parseInt(computedStyle.borderBottomWidth) || 0;
    
    // 使用配置常量
    const chipSize = ANIMATION_CONFIG.CHIP_SIZE;
    const safetyMargin = ANIMATION_CONFIG.SAFETY_MARGIN;
    
    // 计算实际可用区域
    const availableWidth = targetRect.width - paddingLeft - paddingRight - borderLeft - borderRight - chipSize - safetyMargin * 2;
    const availableHeight = ANIMATION_CONFIG.BETTING_AREA_HEIGHT - paddingTop - paddingBottom - borderTop - borderBottom - chipSize - safetyMargin * 2;
    
    // 确保至少有最小的安全空间
    const maxLeft = Math.max(0, availableWidth);
    const maxTop = Math.max(0, availableHeight);
    
    // 如果空间很小，尽量居中放置
    let offsetLeft = paddingLeft + borderLeft + safetyMargin;
    let offsetTop = paddingTop + borderTop + safetyMargin;
    
    // 如果空间足够，使用随机偏移
    if (maxLeft > 0) {
      offsetLeft += Math.floor(Math.random() * maxLeft);
    } else {
      // 如果宽度很小，尽量居中
      offsetLeft = (targetRect.width - chipSize) / 2;
    }
    
    if (maxTop > 0) {
      offsetTop += Math.floor(Math.random() * maxTop);
    } else {
      // 如果高度很小，尽量居中
      offsetTop = (ANIMATION_CONFIG.BETTING_AREA_HEIGHT - chipSize) / 2;
    }
    
    // 最终安全检查，确保不会超出边界
    offsetLeft = Math.min(offsetLeft, targetRect.width - chipSize - borderRight - paddingRight);
    offsetTop = Math.min(offsetTop, ANIMATION_CONFIG.BETTING_AREA_HEIGHT - chipSize - borderBottom - paddingBottom);
    offsetLeft = Math.max(offsetLeft, borderLeft + paddingLeft);
    offsetTop = Math.max(offsetTop, borderTop + paddingTop);
    
    console.debug('筹码严格定位信息:', {
      元素: targetArea.className,
      区域尺寸: { width: targetRect.width, height: ANIMATION_CONFIG.BETTING_AREA_HEIGHT },
      边框: { left: borderLeft, top: borderTop, right: borderRight, bottom: borderBottom },
      内边距: { left: paddingLeft, top: paddingTop, right: paddingRight, bottom: paddingBottom },
      筹码尺寸: chipSize,
      安全边距: safetyMargin,
      最终偏移: { left: offsetLeft, top: offsetTop }
    });
    
    return { left: offsetLeft, top: offsetTop };
  } catch (error) {
    console.error('计算筹码位置出错:', error);
    // 出错时返回默认位置（区域最左上角+3px偏移）
    return { left: 3, top: 3 };
  }
};

/**
 * 获取筹码图标名称
 */
export const getChipIcon = (chipValue: number): string => {
  switch (chipValue) {
    case 1: return 'icon-chip1';
    case 2: return 'icon-chip2';
    case 5: return 'icon-chip5';
    case 10: return 'icon-chip10';
    case 50: return 'icon-chip50';
    case 100: return 'icon-chip100';
    case 200: return 'icon-chip200';
    case 500: return 'icon-chip500';
    default: return 'icon-chip1';
  }
};

/**
 * 获取筹码在底部选择器中的索引位置
 */
export const getChipIndex = (chipValue: number): number => {
  return [1, 2, 5, 10, 50, 100, 200, 500].findIndex(value => value === chipValue);
};

/**
 * 将金额拆分成标准筹码的组合
 * @param amount 总金额
 * @param minChipValue 最小可用筹码值（厅的最小下注限制），默认为1
 * @returns 筹码组合数组，每项包含面额和数量
 * @example splitAmountToChips(2500) => [{value: 500, count: 5}]
 * @example splitAmountToChips(259, 10) => [{value: 200, count: 1}, {value: 50, count: 1}] // 余数9无法用>=10的筹码表示
 */
export const splitAmountToChips = (amount: number, minChipValue: number = 1): Array<{value: number, count: number}> => {
  const standardChips = [500, 200, 100, 50, 10, 5, 2, 1]; // 从大到小排序
  // 🚀 根据厅的最小下注限制过滤可用筹码
  const availableChips = standardChips.filter(v => v >= minChipValue);
  const result: Array<{value: number, count: number}> = [];
  let remaining = amount;
  
  // 获取最小可用筹码（用于确保剩余金额可被整除）
  const smallestChip = availableChips[availableChips.length - 1];
  
  for (const chipValue of availableChips) {
    if (remaining >= chipValue) {
      // 计算最大可用数量
      let count = Math.floor(remaining / chipValue);
      
      // 🔧 关键：确保使用后剩余金额能被最小筹码整除
      // 例如：208 用 200+5 剩 3，但 3%2≠0，所以不用 5，改用 4个2
      while (count > 0) {
        const afterUse = remaining - count * chipValue;
        if (afterUse % smallestChip === 0) {
          break; // 剩余金额可被最小筹码整除
        }
        count--; // 减少使用数量，让更多金额留给小面额筹码
      }
      
      if (count > 0) {
        result.push({ value: chipValue, count });
        remaining -= count * chipValue;
      }
    }
  }
  
  // 🔍 打印拆分结果
  // const chipStr = result.map(c => `${c.count}个${c.value}`).join(' + ');
  // console.log(`[筹码拆分] ${amount} (最小筹码≥${minChipValue}) → ${chipStr}`);
  
  return result;
};

/**
 * 查找筹码模板元素（公共函数）
 * 尝试多种选择器匹配筹码元素，减少代码重复
 * 正确区分主区域（前5个）和展开区域（后3个）的筹码
 */
export const findChipTemplate = (chipValue: number): Element | null => {
  const chipIndex = getChipIndex(chipValue);
  
  if (chipIndex < 0) {
    console.warn('未知的筹码值:', chipValue);
    return null;
  }
  
  let element: Element | null = null;
  
  // 前5个筹码（1, 2, 5, 10, 50）在主区域
  if (chipIndex < 5) {
    element = document.querySelector(`.chip-selector-header .chip-item:nth-child(${chipIndex + 1})`);
    if (element) return element;
  } 
  // 后3个筹码（100, 200, 500）在展开区域
  else {
    // chipIndex: 5->1, 6->2, 7->3
    element = document.querySelector(`.chip-selector-expanded .chip-item:nth-child(${chipIndex - 4})`);
    if (element) return element;
  }
  
  // 备用选择器
  const backupSelectors = [
    `.chip-item[data-value="${chipValue}"]`,
    `[data-chip-value="${chipValue}"]`,
    `.chip-item.chip-active` // 如果找不到，至少返回当前选中的
  ];
  
  for (const selector of backupSelectors) {
    element = document.querySelector(selector);
    if (element) {
      return element;
    }
  }
  
  // 如果都找不到，返回第一个可用筹码
  const allChips = document.querySelectorAll('.chip-item');
  return allChips.length > 0 ? allChips[0] : null;
};

// 筹码 PNG 图片路径映射
const chipPngPaths: Record<number, string> = {
  1: chip1Png,
  2: chip2Png,
  5: chip5Png,
  10: chip10Png,
  50: chip50Png,
  100: chip100Png,
  200: chip200Png,
  500: chip500Png,
};

/**
 * 获取筹码 PNG 图片路径
 */
export const getChipPngPath = (chipValue: number): string => {
  return chipPngPaths[chipValue] || chipPngPaths[1];
};

/**
 * 创建筹码图片元素（使用 PNG）
 */
export const createChipImg = (chipValue: number, size: string = '24'): HTMLImageElement => {
  const img = document.createElement('img');
  img.src = getChipPngPath(chipValue);
  img.width = parseInt(size);
  img.height = parseInt(size);
  img.style.width = `${size}px`;
  img.style.height = `${size}px`;
  img.style.objectFit = 'contain';
  img.style.pointerEvents = 'none';
  img.alt = `chip-${chipValue}`;
  img.draggable = false;
  return img;
};

/**
 * 克隆筹码元素（公共函数）- 兼容旧代码，内部使用 PNG
 * @deprecated 请使用 createChipImg
 */
export const cloneChipSvg = (chipValue: number, size: string = '24'): HTMLImageElement => {
  return createChipImg(chipValue, size);
};

/**
 * 获取区域显示名称（简化版，直接使用映射）
 * @param areaId 拼音区域ID (tian, di, xuan, huang)
 * @param locale 语言环境，默认中文
 */
export const getAreaDisplayName = (areaId: string, locale: string = 'cn'): string => {
  if (locale === 'cn') {
    const cnNames: Record<string, string> = {
      'tian': '天',
      'di': '地',
      'xuan': '玄',
      'huang': '黄'
    };
    return cnNames[areaId] || areaId;
  } else {
    const enNames: Record<string, string> = {
      'tian': 'Heaven',
      'di': 'Earth',
      'xuan': 'Mystery',
      'huang': 'Yellow'
    };
    return enNames[areaId] || areaId;
  }
};

/**
 * 下注验证逻辑
 */
export const validateBet = (chipValue: number, userBalance: number): { valid: boolean, message: string } => {
  if (chipValue <= 0) {
    return { valid: false, message: '请选择有效的筹码' };
  }
  
  if (userBalance < chipValue) {
    return { valid: false, message: '余额不足' };
  }
  
  // 可以添加其他验证规则，如最大下注限额等
  
  return { valid: true, message: '' };
};

/**
 * 更新下注统计信息（当前玩家下注）
 * 只更新 areaTotalValues（当前用户下注统计）
 * allPlayersTotalValues 由接口返回的 sectorTotals 直接设置，不在此处累加
 */
export const updateBettingStats = (chipValue: number, areaId: AreaId): void => {
  // 更新当前玩家统计（用于浮层）
  areaTotalValues.value[areaId] = (areaTotalValues.value[areaId] || 0) + chipValue;
};

/**
 * 删除下注后更新统计信息（当前玩家取消下注）
 * 只更新 areaTotalValues（当前用户下注统计）
 * allPlayersTotalValues 由接口返回的 sectorTotals 直接设置，不在此处修改
 */
export const updateBettingStatsAfterRemoval = (bet: BetRecord): void => {
  const areaId = bet.area as AreaId;
  
  // 更新当前玩家统计
  if (areaId && areaTotalValues.value[areaId]) {
    areaTotalValues.value[areaId] -= bet.value;
    if (areaTotalValues.value[areaId] < 0) {
      areaTotalValues.value[areaId] = 0;
    }
  }
};

/**
 * 筹码飞行动画函数
 */
export const startChipFlyAnimation = async (
  areaId: string,
  chipData: { value: number },
  startElement: HTMLElement,
  targetElement: HTMLElement,
  onComplete: (areaId: string, chipValue: number, finalPosition?: {left: number, top: number}) => void
): Promise<void> => {
  try {
    // 确保GSAP已加载
    const gsap = await loadGSAP();
    if (!gsap) {
      throw new Error('GSAP未正确加载');
    }

    // 获取选中的筹码图标
    const chipIndex = getChipIndex(chipData.value);
    
    // 尝试多种选择器匹配筹码元素
    const selectors = [
      `.chip-item:nth-child(${chipIndex + 1})`,            // 原始选择器
      `.chip-item[data-value="${chipData.value}"]`,       // 按值属性选择
      `.chip-selector .chip-item:nth-child(${chipIndex + 1})`, // 包含父容器
      `[data-chip-value="${chipData.value}"]`             // 按数据属性选择
    ];
    
    // 逐个尝试选择器
    let chipElement = null;
    for (const selector of selectors) {
      chipElement = document.querySelector(selector);
      if (chipElement) {
        break;
      }
    }

    // 如果仍然找不到，列出所有chip-item进行调试
    if (!chipElement) {
      const allChips = document.querySelectorAll('.chip-item');
      allChips.forEach((chip, idx) => {
      });
      
      // 如果有可用的筹码，选择第一个
      if (allChips.length > 0) {
        chipElement = allChips[0];
      }
    }
    
    // 如果找不到筹码元素或起始元素，跳过动画直接执行下注逻辑
    if (!chipElement || !startElement) {
      console.log('找不到筹码元素或起始元素，直接执行下注');
      onComplete(areaId, chipData.value);
      return;
    }

    // 🔧 优化：从对象池获取飞行筹码，避免重复创建DOM
    let flyingChip = chipPool.acquire(chipData.value);
    let isPooledChip = !!flyingChip;
    
    if (!flyingChip) {
      // 对象池为空时，克隆真实筹码元素
      flyingChip = chipElement.cloneNode(true) as HTMLElement;
      flyingChip.classList.add('flying-chip');
      document.body.appendChild(flyingChip);
    } else {
      // 池中筹码需要设置可见
      flyingChip.style.display = 'flex';
      flyingChip.style.opacity = '1';
    }

    // 获取起始位置和目标位置
    const startRect = startElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();

    // 计算筹码落地的最终位置（增加随机位置避免重叠）
    const finalPosition = calculateChipFinalPosition(targetElement, chipData.value, targetRect);

    // 定位筹码初始位置（选中的筹码中心）
    const startX = startRect.left + startRect.width / 2;
    const startY = startRect.top + startRect.height / 2;

    // 定位目标位置（严格定位在区域内部）
    // finalPosition 是永久筹码（18.8px）的左上角偏移
    // 计算永久筹码的中心点坐标
    const permanentChipHalfSize = ANIMATION_CONFIG.PERMANENT_CHIP_SIZE / 2;
    const targetCenterX = targetRect.left + finalPosition.left + permanentChipHalfSize;
    const targetCenterY = targetRect.top + finalPosition.top + permanentChipHalfSize;
    
    // 飞行筹码（24px）的初始中心点
    const flyingChipHalfSize = ANIMATION_CONFIG.FLYING_CHIP_SIZE / 2;
    
    // 调试日志
    console.debug('筹码飞行目标坐标:', { 
      areaRect: { left: targetRect.left, top: targetRect.top, width: targetRect.width, height: targetRect.height },
      chipFinalPosition: finalPosition,
      permanentChipSize: ANIMATION_CONFIG.PERMANENT_CHIP_SIZE,
      targetCenter: { x: targetCenterX, y: targetCenterY }
    });

    // 设置筹码初始状态 - 使用GPU加速的属性
    // 使用 transformOrigin: center 来确保缩放从中心进行
    gsap.set(flyingChip, {
      position: 'fixed',
      zIndex: ANIMATION_CONFIG.Z_INDEX_FLYING,
      left: 0,
      top: 0,
      x: startX - flyingChipHalfSize,
      y: startY - flyingChipHalfSize,
      width: ANIMATION_CONFIG.FLYING_CHIP_SIZE + 'px',
      height: ANIMATION_CONFIG.FLYING_CHIP_SIZE + 'px',
      opacity: 0,
      scale: 1,
      transformOrigin: 'center center',
      force3D: true // 强制GPU加速
    });

    // 创建动画时间轴
    const timeline = gsap.timeline({
      onComplete: () => {
        // 🔧 优化：归还到对象池而不是移除DOM
        if (isPooledChip) {
          chipPool.release(flyingChip, chipData.value);
        } else {
          // 非池化筹码直接移除
          if (flyingChip.parentNode) {
            flyingChip.parentNode.removeChild(flyingChip);
          }
        }
        // 执行下注回调函数，传递最终位置避免二次计算
        onComplete(areaId, chipData.value, finalPosition);
      }
    });

    // 优化的流畅飞行动画 - 使用x/y代替left/top，GPU加速
    const duration = 0.35; // 固定时长，保持一致性
    
    // 计算目标位置：让飞行筹码的中心点对齐到永久筹码的中心点
    // 因为使用 transformOrigin: center，缩放时会保持中心点不变
    const finalX = targetCenterX - flyingChipHalfSize;
    const finalY = targetCenterY - flyingChipHalfSize;
    
    timeline
      // 单步流畅动画：淡入 + 移动 + 缩放同时进行
      .to(flyingChip, {
        duration: duration,
        x: finalX,
        y: finalY,
        scale: ANIMATION_CONFIG.FINAL_SCALE,
        opacity: 1,
        ease: 'power1.out', // 流畅的缓动函数
        force3D: true, // GPU加速
        transformOrigin: 'center center', // 确保从中心缩放
        willChange: 'transform, opacity' // 提示浏览器优化
      });

    return;
  } catch (error) {
    console.error('筹码飞行动画出错:', error);
    // 动画出错时仍然执行下注逻辑
    onComplete(areaId, chipData.value);
  }
};

/**
 * 🚀 同步批量创建筹码（无动画，立即完成）
 * 用于页面恢复时快速显示所有筹码
 */
export const batchCreateChipsSync = (
  bets: Array<{sector: string, amount: number, isCurrentUser: boolean, playerName?: string}>,
  areaElements: Map<string, HTMLElement>
): void => {
  // 使用 DocumentFragment 批量添加，减少重排
  const fragments = new Map<string, DocumentFragment>();
  // 🚀 性能优化：统计每个区域已有的筹码数量（包括已存在的）
  const chipCounts = new Map<string, number>();
  ['tian', 'di', 'xuan', 'huang'].forEach(area => {
    fragments.set(area, document.createDocumentFragment());
    // 统计区域中已有的筹码数量，从该数量开始计数
    const targetArea = areaElements.get(area);
    const existingCount = targetArea 
      ? targetArea.querySelectorAll('.other-player-permanent-chip, .permanent-chip-container').length 
      : 0;
    chipCounts.set(area, existingCount);
  });
  
  bets.forEach(bet => {
    // 🚀 性能优化：限制每个区域的筹码数量
    const currentCount = chipCounts.get(bet.sector) || 0;
    if (currentCount >= PERF_CONFIG.MAX_CHIPS_PER_AREA) {
      return; // 跳过超出限制的筹码（金额仍由统计浮层显示）
    }
    const fragment = fragments.get(bet.sector);
    const targetArea = areaElements.get(bet.sector);
    
    if (!fragment || !targetArea) return;
    
    const targetRect = targetArea.getBoundingClientRect();
    const finalPosition = calculateChipFinalPosition(targetArea, bet.amount, targetRect);
    
    if (bet.isCurrentUser) {
      // 当前用户筹码
      const chipElement = cloneChipSvg(bet.amount, '18.8');
      if (chipElement) {
        const container = document.createElement('div');
        container.className = 'permanent-chip-container';
        const chipSize = ANIMATION_CONFIG.PERMANENT_CHIP_SIZE + 'px';
        Object.assign(container.style, {
          position: 'absolute',
          left: finalPosition.left + 'px',
          top: finalPosition.top + 'px',
          width: chipSize,
          height: chipSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: ANIMATION_CONFIG.Z_INDEX_PERMANENT,
          cursor: 'pointer'
        });
        chipElement.classList.add('permanent-chip');
        chipElement.setAttribute('data-chip-value', bet.amount.toString());
        container.appendChild(chipElement);
        fragment.appendChild(container);
        chipCounts.set(bet.sector, currentCount + 1); // 🚀 递增计数
        
        updateBettingStats(bet.amount, bet.sector as AreaId);
      }
    } else {
      // 其他玩家筹码
      const svgElement = cloneChipSvg(bet.amount, '18.8');
      if (svgElement) {
        svgElement.classList.add('other-player-permanent-chip');
        const chipSize = ANIMATION_CONFIG.PERMANENT_CHIP_SIZE + 'px';
        Object.assign(svgElement.style, {
          position: 'absolute',
          left: finalPosition.left + 'px',
          top: finalPosition.top + 'px',
          width: chipSize,
          height: chipSize
        });
        svgElement.setAttribute('data-chip-value', bet.amount.toString());
        if (bet.playerName) {
          svgElement.setAttribute('data-player', bet.playerName);
        }
        fragment.appendChild(svgElement);
        chipCounts.set(bet.sector, currentCount + 1); // 🚀 递增计数
      }
    }
  });
  
  // 一次性添加所有筹码到DOM
  fragments.forEach((fragment, area) => {
    const targetArea = areaElements.get(area);
    if (targetArea && fragment.childNodes.length > 0) {
      targetArea.appendChild(fragment);
    }
  });
};

/**
 * 清理永久筹码对象池
 */
export const clearPermanentChipPool = () => {
  permanentChipPool.clear();
};

/**
 * 创建用户永久筹码（每次都创建新筹码）
 * @param skipStatsUpdate 是否跳过统计更新（续发等场景已经预先设置了统计值）
 */
export const createPermanentChip = (
  areaId: string, 
  chipValue: number, 
  targetElement: HTMLElement, 
  finalPosition?: {left: number, top: number},
  skipStatsUpdate: boolean = false
): void => {
  try {
    // 获取目标区域
    const targetArea = targetElement;
    if (!targetArea) {
      console.error('找不到目标区域:', areaId);
      return;
    }
    
    // 如果没有传入位置，才重新计算（兼容旧调用）
    if (!finalPosition) {
      const targetRect = targetArea.getBoundingClientRect();
      finalPosition = calculateChipFinalPosition(targetArea, chipValue, targetRect);
    }
    
    // 🎯 每次下注都创建新筹码，直接使用传入的位置避免重复计算
    
    // 使用公共函数克隆筹码SVG
    const chipElement = cloneChipSvg(chipValue, '18.8');
    if (!chipElement) {
      console.error('无法创建筹码元素');
      return;
    }
    
    // 创建筹码容器
    const chipContainer = document.createElement('div');
    chipContainer.className = 'permanent-chip-container';
    const chipSize = ANIMATION_CONFIG.PERMANENT_CHIP_SIZE + 'px';
    Object.assign(chipContainer.style, {
      position: 'absolute',
      left: finalPosition.left + 'px',
      top: finalPosition.top + 'px',
      width: chipSize,
      height: chipSize,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: ANIMATION_CONFIG.Z_INDEX_PERMANENT,
      cursor: 'pointer',
      boxSizing: 'border-box',
      overflow: 'hidden',
      maxWidth: chipSize,
      maxHeight: chipSize
    });
    
    // 设置共同属性
    chipElement.classList.add('permanent-chip');
    chipElement.setAttribute('data-chip-value', chipValue.toString());
    
    // 组装DOM
    chipContainer.appendChild(chipElement);
    targetArea.appendChild(chipContainer);
    
    // 更新下注统计（续发等场景已经预先设置了统计值，跳过更新）
    if (!skipStatsUpdate) {
      updateBettingStats(chipValue, areaId as AreaId);
    }
    
  } catch (error) {
    console.error('创建永久筹码出错:', error);
  }
};

/**
 * 其他玩家下注动画
 * @param player 玩家信息
 * @param areaId 下注区域ID
 * @param chipValue 下注金额
 * @param sourceElement 起始元素
 * @param targetElement 目标元素
 * @param onComplete 完成回调
 * @param minBetLimit 厅的最小下注限制，用于筹码拆分
 */
export const startOtherPlayerBettingAnimation = async (
  player: Player,
  areaId: string, 
  chipValue: number,
  sourceElement: HTMLElement,
  targetElement: HTMLElement,
  onComplete: (areaId: string, chipValue: number, playerName: string) => void,
  minBetLimit: number = 1
): Promise<void> => {
  try {
    // 确保GSAP已加载
    const gsap = await loadGSAP();
    if (!gsap) {
      throw new Error('GSAP未正确加载');
    }
    
    // 将总金额拆分成标准筹码组合（根据厅的最小下注限制过滤可用筹码）
    const chipCombo = splitAmountToChips(chipValue, minBetLimit);
    const totalChips = chipCombo.reduce((sum, c) => sum + c.count, 0);
    
    // 🚀 动态限流：只限制动画数量，不限制筹码数量（保证金额显示完整）
    const maxAnimatedChips = isTurboMode 
      ? PERF_CONFIG.TURBO_MODE_CHIPS 
      : PERF_CONFIG.MAX_CHIPS_PER_BET;
    
    let animatedChipIndex = 0; // 跟踪已分配动画的筹码数量
    
    // 获取起始位置（在线人数按钮）
    const sourceRect = sourceElement.getBoundingClientRect();
    const startX = sourceRect.left + sourceRect.width / 2;
    const startY = sourceRect.top + sourceRect.height / 2;
    
    // 获取目标区域位置
    const targetRect = targetElement.getBoundingClientRect();
    
    let chipsLanded = 0;
    
    // 🎬 使用RAF调度器批量处理动画
    for (const { value, count } of chipCombo) {
      for (let i = 0; i < count; i++) {
        const currentChipIndex = animatedChipIndex++;
        
        // 🚀 超过动画限制的筹码：直接创建，不播放动画（保证金额完整显示）
        if (currentChipIndex >= maxAnimatedChips) {
          const directPosition = calculateChipFinalPosition(targetElement, value, targetRect);
          createOtherPlayerPermanentChip(areaId, value, player.name, player.avatar, targetElement, directPosition);
          chipsLanded++;
          if (chipsLanded === totalChips) {
            onComplete(areaId, chipValue, player.name);
          }
          continue;
        }
        
        // 将动画任务加入RAF队列
        rafScheduler.enqueue(() => {
            // 🚦 并发控制：达到上限则跳过动画，但仍创建永久筹码
          if (activeAnimCount >= PERF_CONFIG.MAX_CONCURRENT_ANIMS) {
            // 🔧 修复：跳过动画时仍需创建永久筹码（计算位置后传入）
            const skipPosition = calculateChipFinalPosition(targetElement, value, targetRect);
            createOtherPlayerPermanentChip(areaId, value, player.name, player.avatar, targetElement, skipPosition);
            
            chipsLanded++;
            if (chipsLanded === totalChips) {
              onComplete(areaId, chipValue, player.name);
            }
            return;
          }
          
          // 从对象池获取筹码
          const flyingChip = chipPool.acquire(value);
          if (!flyingChip) {
            // 🔧 修复：获取筹码失败时仍需创建永久筹码（计算位置后传入）
            const fallbackPosition = calculateChipFinalPosition(targetElement, value, targetRect);
            createOtherPlayerPermanentChip(areaId, value, player.name, player.avatar, targetElement, fallbackPosition);
            
            chipsLanded++;
            if (chipsLanded === totalChips) {
              onComplete(areaId, chipValue, player.name);
            }
            return;
          }
          
          // 计算位置
          const finalPosition = calculateChipFinalPosition(targetElement, value, targetRect);
          const targetX = targetRect.left + finalPosition.left;
          const targetY = targetRect.top + finalPosition.top;
          
          // 🚀 涡轮模式：减少动画时长
          const duration = isTurboMode 
            ? 0.15 
            : ANIMATION_CONFIG.ANIMATION_DURATION_MIN + Math.random() * 0.1;
          
          activeAnimCount++;
          
          // 设置初始位置
          gsap.set(flyingChip, {
            position: 'fixed',
            zIndex: ANIMATION_CONFIG.Z_INDEX_FLYING,
            left: startX + 'px',
            top: startY + 'px',
            opacity: 1
          });
          
          // 飞行动画
          gsap.to(flyingChip, {
            left: targetX + 'px',
            top: targetY + 'px',
            duration: duration,
            ease: isTurboMode ? 'power1.out' : 'none',
            onComplete: () => {
              activeAnimCount--;
              // 归还到对象池
              chipPool.release(flyingChip, value);
              // 创建永久筹码（传入与飞行动画相同的位置，避免跳动）
              createOtherPlayerPermanentChip(areaId, value, player.name, player.avatar, targetElement, finalPosition);
              chipsLanded++;
              if (chipsLanded === totalChips) {
                onComplete(areaId, chipValue, player.name);
              }
            }
          });
        });
      }
    }
    
  } catch (error) {
    console.error('其他玩家下注动画出错:', error);
    // 动画出错时仍然执行下注逻辑
    onComplete(areaId, chipValue, player.name);
  }
};

/**
 * 在目标区域创建其他玩家永久筹码（使用公共函数简化）
 * @param passedPosition 可选，传入已计算的位置避免重新计算（防止筹码跳动）
 */
export const createOtherPlayerPermanentChip = (
  areaId: string, 
  chipValue: number, 
  playerName: string, 
  playerAvatar: string, 
  targetElement: HTMLElement,
  passedPosition?: { left: number; top: number }
): void => {
  try {
    if (!targetElement) {
      console.error('找不到目标区域:', areaId);
      return;
    }
    
    // 🚀 性能优化：限制每个区域的筹码DOM数量，超出时删除最早的筹码（无感处理）
    const existingChips = targetElement.querySelectorAll('.other-player-permanent-chip, .permanent-chip-container');
    if (existingChips.length >= PERF_CONFIG.MAX_CHIPS_PER_AREA) {
      // 删除最早的筹码（第一个），为新筹码腾出空间
      const oldestChip = existingChips[0];
      if (oldestChip && oldestChip.parentNode) {
        oldestChip.parentNode.removeChild(oldestChip);
      }
    }
    
    // 🔧 优先使用传入的位置，避免重新计算导致筹码跳动
    let finalPosition = passedPosition;
    if (!finalPosition) {
      const targetRect = targetElement.getBoundingClientRect();
      finalPosition = calculateChipFinalPosition(targetElement, chipValue, targetRect);
    }
    
    // 使用公共函数克隆筹码SVG
    const svgElement = cloneChipSvg(chipValue, '18.8');
    if (!svgElement) {
      console.error('创建其他玩家永久筹码失败');
      return;
    }
    
    // 设置SVG元素属性和样式
    svgElement.classList.add('other-player-permanent-chip');
    const chipSize = ANIMATION_CONFIG.PERMANENT_CHIP_SIZE + 'px';
    Object.assign(svgElement.style, {
      position: 'absolute',
      left: finalPosition.left + 'px',
      top: finalPosition.top + 'px',
      width: chipSize,
      height: chipSize
    });
    svgElement.setAttribute('data-chip-value', chipValue.toString());
    svgElement.setAttribute('data-player', playerName);
    svgElement.setAttribute('title', `${playerName}: ${chipValue}`);
    
    // 添加到DOM
    targetElement.appendChild(svgElement);
    
    // ✅ 验证筹码是否成功添加到DOM
    if (!targetElement.contains(svgElement)) {
      console.error('筹码添加到DOM失败:', areaId, chipValue);
      return;
    }
    // 注意：allPlayersTotalValues 由接口返回的 sectorTotals 直接设置，不在此处累加
    
  } catch (error) {
    console.error('创建其他玩家永久筹码出错:', error);
  }
};

/**
 * 停止所有动画并清理飞行中的筹码
 * 用于切换房间时清理动画状态
 */
export const stopAllAnimations = (): void => {
  try {
    // 1. 清理RAF调度器
    rafScheduler.clear();
    activeAnimCount = 0;
    isTurboMode = false;
    
    // 2. 清理对象池
    chipPool.clear();
    
    // 3. 停止所有GSAP动画
    if (gsap && typeof gsap.killTweensOf === 'function') {
      // 停止所有目标上的动画
      gsap.killTweensOf('*');
    }
    
    // 4. 清理所有飞行中的筹码元素
    const flyingChips = document.querySelectorAll('.flying-chip, .flying-chip-pooled');
    flyingChips.forEach(chip => {
      if (chip.parentNode) {
        chip.parentNode.removeChild(chip);
      }
    });
    if (flyingChips.length > 0) {
      console.log(`清理了 ${flyingChips.length} 个飞行筹码`);
    }
    
    // 5. 清理所有可能残留的固定定位元素（以防动画未完成）
    const fixedChips = document.querySelectorAll('[style*="position: fixed"][style*="z-index: 50"]');
    fixedChips.forEach(chip => {
      // 只清理筹码相关的元素，避免误删其他固定定位元素
      if (chip.classList.contains('flying-chip') || chip.querySelector('.icon-chip')) {
        if (chip.parentNode) {
          chip.parentNode.removeChild(chip);
        }
      }
    });
    if (fixedChips.length > 0) {
    }
    
  } catch (error) {
    console.error('[停止动画] 清理过程出错:', error);
  }
};

/**
 * 获取性能统计信息
 */
export const getPerformanceStats = () => {
  const poolStats = chipPool.getStats();
  const rafStats = rafScheduler.getStats();
  
  return {
    ...poolStats,
    ...rafStats,
    status: isTurboMode ? '⚡ 涡轮模式' : '✅ 正常模式',
    recommendation: activeAnimCount > 15 ? '建议减少在线人数或房间数' : '性能良好'
  };
};

/**
 * 批量同时下注动画（所有筹码同时飞行）
 * 用于 EMIT_NIUNIU_JOIN 返回后处理历史下注数据
 * @param bets 下注数据数组
 * @param sourceElement 起始元素（在线人数按钮）
 * @param areaElements 区域元素Map
 * @param onAllComplete 所有动画完成回调
 * @param minBetLimit 厅的最小下注限制
 */
export const batchBettingAnimationSimultaneous = async (
  bets: Array<{sector: string, amount: number, playerName: string}>,
  sourceElement: HTMLElement,
  areaElements: Map<string, HTMLElement>,
  onAllComplete?: () => void,
  minBetLimit: number = 1  // 
): Promise<void> => {
  if (bets.length === 0) {
    onAllComplete?.();
    return;
  }
  
  try {
    const gsapInstance = await loadGSAP();
    if (!gsapInstance) {
      console.error('[批量动画] GSAP未加载');
      onAllComplete?.();
      return;
    }
    
    // 获取起始位置
    const sourceRect = sourceElement.getBoundingClientRect();
    const startX = sourceRect.left + sourceRect.width / 2;
    const startY = sourceRect.top + sourceRect.height / 2;
    
    // 预处理：将所有下注拆分成筹码
    interface ChipAnimData {
      sector: string;
      chipValue: number;
      playerName: string;
      targetArea: HTMLElement;
      targetRect: DOMRect;
    }
    
    const allChips: ChipAnimData[] = [];
    
    // 🚀 性能优化：统计每个区域已有的筹码数量，限制总数
    const areaChipCounts = new Map<string, number>();
    ['tian', 'di', 'xuan', 'huang'].forEach(area => {
      const targetArea = areaElements.get(area);
      const existingCount = targetArea 
        ? targetArea.querySelectorAll('.other-player-permanent-chip, .permanent-chip-container').length 
        : 0;
      areaChipCounts.set(area, existingCount);
    });
    
    bets.forEach(bet => {
      const targetArea = areaElements.get(bet.sector);
      if (!targetArea) return;
      
      // 🚀 检查该区域是否已达到上限
      const currentAreaCount = areaChipCounts.get(bet.sector) || 0;
      if (currentAreaCount >= PERF_CONFIG.MAX_CHIPS_PER_AREA) {
        return; // 跳过该区域的筹码
      }
      
      const targetRect = targetArea.getBoundingClientRect();
      const chipCombo = splitAmountToChips(bet.amount, minBetLimit);  // 🚀 传入厅的最小下注限制
      
      // 限制每笔下注的筹码数量
      const maxChips = isTurboMode ? PERF_CONFIG.TURBO_MODE_CHIPS : PERF_CONFIG.MAX_CHIPS_PER_BET;
      let chipCount = 0;
      
      for (const { value, count } of chipCombo) {
        for (let i = 0; i < count && chipCount < maxChips; i++) {
          // 🚀 再次检查区域上限
          const areaCount = areaChipCounts.get(bet.sector) || 0;
          if (areaCount >= PERF_CONFIG.MAX_CHIPS_PER_AREA) {
            break;
          }
          
          allChips.push({
            sector: bet.sector,
            chipValue: value,
            playerName: bet.playerName,
            targetArea,
            targetRect
          });
          chipCount++;
          areaChipCounts.set(bet.sector, areaCount + 1);
        }
      }
    });
    
    console.log(`[批量动画] 共 ${allChips.length} 个筹码同时飞行（每区域上限 ${PERF_CONFIG.MAX_CHIPS_PER_AREA}）`);
    
    if (allChips.length === 0) {
      onAllComplete?.();
      return;
    }
    
    let completedCount = 0;
    const totalCount = allChips.length;
    
    // 🚀 同时启动所有筹码动画
    allChips.forEach((chipData, index) => {
      // 从对象池获取筹码
      const flyingChip = chipPool.acquire(chipData.chipValue);
      if (!flyingChip) {
        // 🔧 修复：获取筹码失败时仍需创建永久筹码
        const fallbackPosition = calculateChipFinalPosition(chipData.targetArea, chipData.chipValue, chipData.targetRect);
        createOtherPlayerPermanentChip(chipData.sector, chipData.chipValue, chipData.playerName, '', chipData.targetArea, fallbackPosition);
        
        completedCount++;
        if (completedCount === totalCount) onAllComplete?.();
        return;
      }
      
      // 计算最终位置
      const finalPosition = calculateChipFinalPosition(
        chipData.targetArea, 
        chipData.chipValue, 
        chipData.targetRect
      );
      const targetX = chipData.targetRect.left + finalPosition.left;
      const targetY = chipData.targetRect.top + finalPosition.top;
      
      // 动画时长（添加微小随机差异使动画更自然）
      const duration = isTurboMode 
        ? 0.2 + Math.random() * 0.05
        : 0.35 + Math.random() * 0.1;
      
      activeAnimCount++;
      
      // 设置初始位置
      gsapInstance.set(flyingChip, {
        position: 'fixed',
        zIndex: ANIMATION_CONFIG.Z_INDEX_FLYING,
        left: startX + 'px',
        top: startY + 'px',
        opacity: 1,
        display: 'flex'
      });
      
      // 🎯 同时启动飞行动画（无延迟）
      gsapInstance.to(flyingChip, {
        left: targetX + 'px',
        top: targetY + 'px',
        duration: duration,
        ease: 'power2.out',
        onComplete: () => {
          activeAnimCount--;
          
          // 归还到对象池
          chipPool.release(flyingChip, chipData.chipValue);
          
          // 创建永久筹码（传入与飞行动画相同的位置，避免跳动）
          createOtherPlayerPermanentChip(
            chipData.sector, 
            chipData.chipValue, 
            chipData.playerName, 
            '', 
            chipData.targetArea,
            finalPosition
          );
          
          completedCount++;
          if (completedCount === totalCount) {
            console.log('[批量动画] 所有筹码动画完成');
            onAllComplete?.();
          }
        }
      });
    });
    
  } catch (error) {
    console.error('[批量动画] 执行失败:', error);
    onAllComplete?.();
  }
};

/**
 * 
 * 
 * @param sectorAmounts 
 * @param sourceElement 
 * @param areaElements 
 * @param onAllComplete 
 * @param minBetLimit 
 */
export const batchCurrentPlayerBettingAnimation = async (
  sectorAmounts: Record<string, number>,
  sourceElement: HTMLElement,
  areaElements: Map<string, HTMLElement>,
  onAllComplete?: () => void,
  minBetLimit: number = 1
): Promise<void> => {
  const entries = Object.entries(sectorAmounts).filter(([_, amount]) => amount > 0);
  
  if (entries.length === 0) {
    onAllComplete?.();
    return;
  }
  
  try {
    const gsapInstance = await loadGSAP();
    if (!gsapInstance) {
      // 无动画时直接创建筹码
      entries.forEach(([sector, amount]) => {
        const targetArea = areaElements.get(sector);
        if (targetArea) {
          const chipCombo = splitAmountToChips(amount, minBetLimit);
          chipCombo.forEach(({ value, count }) => {
            for (let i = 0; i < count; i++) {
              createPermanentChip(sector, value, targetArea);
            }
          });
        }
      });
      onAllComplete?.();
      return;
    }
    
    // 获取起始位置
    const sourceRect = sourceElement.getBoundingClientRect();
    const startX = sourceRect.left + sourceRect.width / 2;
    const startY = sourceRect.top + sourceRect.height / 2;
    
    // 预处理：将所有下注拆分成筹码
    interface ChipAnimData {
      sector: string;
      chipValue: number;
      targetArea: HTMLElement;
      targetRect: DOMRect;
    }
    
    const allChips: ChipAnimData[] = [];
    
    entries.forEach(([sector, amount]) => {
      const targetArea = areaElements.get(sector);
      if (!targetArea) return;
      
      const targetRect = targetArea.getBoundingClientRect();
      const chipCombo = splitAmountToChips(amount, minBetLimit);
      
      // 限制每个区域的筹码数量
      const maxChips = isTurboMode ? PERF_CONFIG.TURBO_MODE_CHIPS : PERF_CONFIG.MAX_CHIPS_PER_BET;
      let chipCount = 0;
      
      for (const { value, count } of chipCombo) {
        for (let i = 0; i < count && chipCount < maxChips; i++) {
          allChips.push({
            sector,
            chipValue: value,
            targetArea,
            targetRect
          });
          chipCount++;
        }
      }
    });
    
    if (allChips.length === 0) {
      onAllComplete?.();
      return;
    }
    
    let completedCount = 0;
    const totalCount = allChips.length;
    
    // 同时启动所有筹码动画
    allChips.forEach((chipData) => {
      // 从对象池获取筹码
      let flyingChip = chipPool.acquire(chipData.chipValue);
      let isPooledChip = !!flyingChip;
      
      if (!flyingChip) {
        // 对象池为空时，克隆筹码
        const chipTemplate = findChipTemplate(chipData.chipValue);
        if (chipTemplate) {
          flyingChip = chipTemplate.cloneNode(true) as HTMLElement;
          flyingChip.classList.add('flying-chip');
          document.body.appendChild(flyingChip);
        } else {
          completedCount++;
          createPermanentChip(chipData.sector, chipData.chipValue, chipData.targetArea, undefined, true);
          if (completedCount === totalCount) onAllComplete?.();
          return;
        }
      }
      
      // 计算最终位置
      const finalPosition = calculateChipFinalPosition(
        chipData.targetArea, 
        chipData.chipValue, 
        chipData.targetRect
      );
      const targetX = chipData.targetRect.left + finalPosition.left;
      const targetY = chipData.targetRect.top + finalPosition.top;
      
      const duration = isTurboMode 
         ? 0.3 + Math.random() * 0.05
        : 0.5 + Math.random() * 0.1;
      
      activeAnimCount++;
      
      gsapInstance.set(flyingChip, {
        position: 'fixed',
        zIndex: ANIMATION_CONFIG.Z_INDEX_FLYING,
        left: startX + 'px',
        top: startY + 'px',
        opacity: 1,
        display: 'flex',
        width: ANIMATION_CONFIG.FLYING_CHIP_SIZE + 'px',
        height: ANIMATION_CONFIG.FLYING_CHIP_SIZE + 'px'
      });
      
      gsapInstance.to(flyingChip, {
        left: targetX + 'px',
        top: targetY + 'px',
        duration: duration,
        ease: 'power2.out',
        onComplete: () => {
          activeAnimCount--;
          
          if (isPooledChip) {
            chipPool.release(flyingChip!, chipData.chipValue);
          } else if (flyingChip?.parentNode) {
            flyingChip.parentNode.removeChild(flyingChip);
          }
          
          createPermanentChip(chipData.sector, chipData.chipValue, chipData.targetArea, finalPosition, true);
          
          completedCount++;
          if (completedCount === totalCount) {
            onAllComplete?.();
          }
        }
      });
    });
    
  } catch (error) {
    console.error('[当前玩家批量动画] 执行失败:', error);
    entries.forEach(([sector, amount]) => {
      const targetArea = areaElements.get(sector);
      if (targetArea) {
        const chipCombo = splitAmountToChips(amount, minBetLimit);
        chipCombo.forEach(({ value, count }) => {
          for (let i = 0; i < count; i++) {
            createPermanentChip(sector, value, targetArea, undefined, true);
          }
        });
      }
    });
    onAllComplete?.();
  }
};

// 导出统一的下注动画模块
const bettingAnimation = {
  // 响应式状态
  areaTotalValues,
  allPlayersTotalValues,
  activeBets,
  
  // GSAP加载
  loadGSAP,
  
  // 筹码动画核心函数
  startChipFlyAnimation,
  createPermanentChip,
  
  // 性能统计
  getPerformanceStats,
};

export { bettingAnimation };
export default bettingAnimation;